import { Effort, Priority, ProgressStatus, RecommendationOutput } from '@amzn/aws-assessment-recommendation-service-client';
import { TableProps } from '@amzn/awsui-components-react';

import { AppLabels } from '../../../../../../common/AppLabels';

export const getEffortOptions = (appLabels: AppLabels) => [
    {
        value: Effort.SHORT,
        label: appLabels.common.short,
    },
    {
        value: Effort.MEDIUM,
        label: appLabels.common.medium,
    },
    {
        value: Effort.LONG,
        label: appLabels.common.long,
    },
    {
        value: Effort.NA,
        label: appLabels.common.na,
    },
];
export const getPriorityOptions = (appLabels: AppLabels) => [
    {
        value: Priority.HIGH,
        label: appLabels.common.high,
    },
    {
        value: Priority.MEDIUM,
        label: appLabels.common.medium,
    },
    {
        value: Priority.LOW,
        label: appLabels.common.low,
    },
    {
        value: Priority.NA,
        label: appLabels.common.na,
    },
];
export const getProgressStatusOptions = (appLabels: AppLabels) => [
    {
        value: ProgressStatus.NOT_STARTED,
        label: appLabels.assessment.results.review_all.report.recommended_actions.not_started,
    },
    {
        value: ProgressStatus.IN_PROGRESS,
        label: appLabels.assessment.results.review_all.report.recommended_actions.in_progress,
    },
    {
        value: ProgressStatus.COMPLETED,
        label: appLabels.assessment.results.review_all.report.recommended_actions.completed,
    },
];

const SEVEN_PERCENT_COLUMN_WIDTH = '7%';
const TEN_PERCENT_COLUMN_WIDTH = '10%';
const FORTY_PERCENT_COLUMN_WIDTH = '40%';

export const getRecommendationTableColumnDefs = (appLabels: AppLabels): TableProps.ColumnDefinition<RecommendationOutput>[] => {
    const effortOptions = getEffortOptions(appLabels);
    const priorityOptions = getPriorityOptions(appLabels);
    const progressStatusOptions = getProgressStatusOptions(appLabels);
    return [
        {
            width: FORTY_PERCENT_COLUMN_WIDTH,
            id: 'recommendedAction',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_action,
            cell: (item) => item.verbiage,
            sortingComparator: (a, b) => (a.verbiage > b.verbiage ? -1 : 1),
            sortingField: 'recommendedAction',
        },
        {
            width: SEVEN_PERCENT_COLUMN_WIDTH,
            id: 'priority',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_priority,
            cell: (item) => priorityOptions.find((option) => item.priority === option.value)?.label,
            sortingComparator: (a, b) =>
                a.priority && b.priority
                    ? priorityOptions.findIndex((option) => option.value === a.priority) -
                      priorityOptions.findIndex((option) => option.value === b.priority)
                    : 1,
            sortingField: 'priority',
        },
        {
            width: SEVEN_PERCENT_COLUMN_WIDTH,
            id: 'effort',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_effort,
            cell: (item) => effortOptions.find((option) => item.effort === option.value)?.label,
            sortingComparator: (a, b) =>
                a.effort
                    ? effortOptions.findIndex((option) => option.value === a.effort) - effortOptions.findIndex((option) => option.value === b.effort)
                    : 1,
            sortingField: 'effort',
        },
        {
            width: TEN_PERCENT_COLUMN_WIDTH,
            id: 'targetDate',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_target_date,
            cell: (item) => item.targetDate,
            sortingComparator: (a, b) => (a.targetDate > b.targetDate ? -1 : 1),
            sortingField: 'targetDate',
        },
        {
            maxWidth: FORTY_PERCENT_COLUMN_WIDTH,
            id: 'benefit',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_benefit,
            cell: (item) => item.benefit,
            sortingComparator: (a, b) => (a.benefit > b.benefit ? -1 : 1),
            sortingField: 'benefit',
        },
        {
            width: TEN_PERCENT_COLUMN_WIDTH,
            id: 'progressStatus',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_progress,
            cell: (item) => progressStatusOptions.find((option) => item.progressStatus === option.value)?.label,
            sortingComparator: (a, b) =>
                a.progressStatus
                    ? progressStatusOptions.findIndex((option) => option.value === a.progressStatus) -
                      progressStatusOptions.findIndex((option) => option.value === b.progressStatus)
                    : 1,
            sortingField: 'progressStatus',
        },
        {
            width: SEVEN_PERCENT_COLUMN_WIDTH,
            id: 'actionOwner',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_action_owner,
            cell: (item) => item.actionOwner,
            sortingComparator: (a, b) => (a.actionOwner > b.actionOwner ? -1 : 1),
            sortingField: 'actionOwner',
        },
    ];
};

export const getPrescribedRecommendationTableColumnDefs = (appLabels: AppLabels): TableProps.ColumnDefinition<RecommendationOutput>[] => {
    return [
        {
            id: 'recommendedAction',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_action,
            cell: (item) => item.verbiage,
            sortingComparator: (a, b) => (a.verbiage > b.verbiage ? -1 : 1),
            sortingField: 'recommendedAction',
        },
        {
            id: 'benefit',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_benefit,
            cell: (item) => item.benefit,
            sortingComparator: (a, b) => (a.benefit > b.benefit ? -1 : 1),
            sortingField: 'benefit',
        },
        {
            id: 'recommendationOwner',
            header: appLabels.assessment.results.review_all.report.recommended_actions.grid.column_recommendation_owner,
            cell: (item) => item.recommendationOwner,
            sortingComparator: (a, b) => (a.recommendationOwner > b.recommendationOwner ? -1 : 1),
            sortingField: 'recommendationOwner',
        },
    ];
};
